
import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';

export interface GdprDeletionConfig {
  enablePoints?: boolean;
  enableNotifications?: boolean;
  validateCompliance?: boolean;
  autoProcessPhase2?: boolean;
  cooldownPeriodDays?: number;
}

export interface GdprDeletionResult {
  success: boolean;
  phase: 'validation' | 'anonymization' | 'auth_deletion' | 'completed';
  requestId?: string;
  userId: string;
  error?: string;
  details?: {
    anonymizationCompleted?: boolean;
    authUserDeleted?: boolean;
    storageCleanup?: boolean;
    complianceValidated?: boolean;
    pointsAwarded?: number;
  };
}

export interface GdprValidationResult {
  isCompliant: boolean;
  userId: string;
  issues: string[];
  tablesChecked: number;
  detailedCounts?: Record<string, number>;
}

export interface GdprDeletionRequest {
  id: string;
  userId: string;
  email: string;
  status: 'pending' | 'cooling_off' | 'processing' | 'pending_auth_deletion' | 'completed' | 'cancelled' | 'failed' | 'auth_deletion_failed';
  requestedAt: string;
  coolingOffUntil: string | null;
  auditLog: any;
}

export class GdprDeletionService {
  private static instance: GdprDeletionService;
  
  public static getInstance(): GdprDeletionService {
    if (!GdprDeletionService.instance) {
      GdprDeletionService.instance = new GdprDeletionService();
    }
    return GdprDeletionService.instance;
  }

  /**
   * Get persisted configuration from database
   */
  private async getPersistedConfig(): Promise<Partial<GdprDeletionConfig>> {
    try {
      const { data: autoProcessSetting } = await supabase
        .from('app_settings')
        .select('value')
        .eq('key', 'auto_process_phase2_enabled')
        .single();

      const { data: cooldownSetting } = await supabase
        .from('app_settings')
        .select('value')
        .eq('key', 'deletion_cooling_off_days')
        .single();

      return {
        autoProcessPhase2: autoProcessSetting?.value === true || autoProcessSetting?.value === 'true',
        cooldownPeriodDays: cooldownSetting?.value ? parseInt(String(cooldownSetting.value)) : 7
      };
    } catch (error) {
      console.error('Error getting persisted config:', error);
      return {};
    }
  }

  /**
   * Request user deletion with configurable options
   */
  async requestDeletion(
    userId: string, 
    email: string, 
    config: GdprDeletionConfig = {}
  ): Promise<GdprDeletionResult> {
    try {
      const persistedConfig = await this.getPersistedConfig();
      const finalConfig = { ...persistedConfig, ...config };

      const { data, error } = await supabase.functions.invoke('request-user-deletion', {
        body: {
          userId,
          email,
          config: finalConfig
        }
      });

      if (error) throw error;
      
      return {
        success: data.success,
        phase: 'validation',
        userId,
        requestId: data.request_id,
        error: data.success ? undefined : data.error
      };
    } catch (error: any) {
      return {
        success: false,
        phase: 'validation',
        userId,
        error: error.message
      };
    }
  }

  /**
   * Validate GDPR compliance for a user
   */
  async validateCompliance(userId: string): Promise<GdprValidationResult> {
    try {
      const { data, error } = await supabase.rpc('validate_gdpr_deletion_compliance', {
        target_user_id: userId
      });

      if (error) throw error;

      const validationData = typeof data === 'string' ? JSON.parse(data) : data;
      
      return {
        isCompliant: validationData.gdpr_compliant,
        userId,
        issues: validationData.issues || [],
        tablesChecked: validationData.tables_validated || 22,
        detailedCounts: validationData.detailed_counts
      };
    } catch (error: any) {
      return {
        isCompliant: false,
        userId,
        issues: [`Validation failed: ${error.message}`],
        tablesChecked: 0
      };
    }
  }

  /**
   * Execute Phase 1: Data anonymization
   */
  async executePhase1(
    userId: string, 
    config: GdprDeletionConfig = {}
  ): Promise<GdprDeletionResult> {
    try {
      const persistedConfig = await this.getPersistedConfig();
      const finalConfig = { ...persistedConfig, ...config };

      const { data, error } = await supabase.rpc('execute_complete_gdpr_deletion', {
        target_user_id: userId,
        process_admin_id: null
      });

      if (error) throw error;

      // Type guard for data structure
      const deletionResult = data as any;

      if (!deletionResult?.success) {
        return {
          success: false,
          phase: 'anonymization',
          userId,
          error: deletionResult?.error || 'Phase 1 execution failed'
        };
      }

      return {
        success: true,
        phase: 'anonymization',
        userId,
        details: {
          anonymizationCompleted: true,
          complianceValidated: deletionResult.gdpr_compliant
        }
      };
    } catch (error: any) {
      return {
        success: false,
        phase: 'anonymization',
        userId,
        error: error.message
      };
    }
  }

  /**
   * Execute Phase 2: Auth user deletion
   */
  async executePhase2(
    userId: string, 
    config: GdprDeletionConfig = {}
  ): Promise<GdprDeletionResult> {
    try {
      const { data, error } = await supabase.functions.invoke('delete-user', {
        body: {
          userId
        }
      });

      if (error) throw error;

      // Type guard for data structure
      const deleteResult = data as any;

      if (!deleteResult?.success) {
        return {
          success: false,
          phase: 'auth_deletion',
          userId,
          error: deleteResult?.error || 'Phase 2 execution failed'
        };
      }

      return {
        success: true,
        phase: 'auth_deletion',
        userId,
        details: {
          authUserDeleted: deleteResult.auth_user_deleted,
          storageCleanup: deleteResult.deletion_details?.storage_cleanup_successful
        }
      };
    } catch (error: any) {
      return {
        success: false,
        phase: 'auth_deletion',
        userId,
        error: error.message
      };
    }
  }

  /**
   * Get deletion requests with filtering
   */
  async getDeletionRequests(filters?: {
    status?: string;
    userId?: string;
    email?: string;
  }): Promise<GdprDeletionRequest[]> {
    try {
      let query = supabase
        .from('data_deletion_requests')
        .select('*')
        .order('requested_at', { ascending: false });

      if (filters?.status) {
        query = query.eq('status', filters.status);
      }
      if (filters?.userId) {
        query = query.eq('user_id', filters.userId);
      }
      if (filters?.email) {
        query = query.ilike('email', `%${filters.email}%`);
      }

      const { data, error } = await query;

      if (error) throw error;

      return data?.map(request => ({
        id: request.id,
        userId: request.user_id,
        email: request.email,
        status: request.status as any,
        requestedAt: request.requested_at,
        coolingOffUntil: request.cooling_off_until,
        auditLog: request.audit_log
      })) || [];
    } catch (error: any) {
      console.error('Error fetching deletion requests:', error);
      return [];
    }
  }

  /**
   * Confirm deletion request (complete cooling off period)
   */
  async confirmDeletion(
    confirmationToken: string,
    config: GdprDeletionConfig = {}
  ): Promise<GdprDeletionResult> {
    try {
      const persistedConfig = await this.getPersistedConfig();
      const finalConfig = { ...persistedConfig, ...config };

      const { data, error } = await supabase.functions.invoke('confirm-user-deletion', {
        body: {
          token: confirmationToken,
          config: finalConfig
        }
      });

      if (error) throw error;
      
      return {
        success: data.success,
        phase: 'validation',
        userId: '',
        error: data.success ? undefined : data.error
      };
    } catch (error: any) {
      return {
        success: false,
        phase: 'validation',
        userId: '',
        error: error.message
      };
    }
  }

  /**
   * Process all pending deletion requests automatically
   */
  async processAllPendingRequests(
    config: GdprDeletionConfig = {}
  ): Promise<GdprDeletionResult[]> {
    try {
      const persistedConfig = await this.getPersistedConfig();
      const finalConfig = { ...persistedConfig, ...config };

      const { data, error } = await supabase.functions.invoke('process-deletion-requests', {
        body: {
          manual_trigger: true,
          config: finalConfig
        }
      });

      if (error) throw error;
      
      return data.results?.map((result: any) => ({
        success: result.success,
        phase: result.phase,
        userId: result.user_id,
        requestId: result.request_id,
        error: result.error
      })) || [];
    } catch (error: any) {
      console.error('Error processing pending requests:', error);
      return [];
    }
  }
}

// Export singleton instance
export const gdprService = GdprDeletionService.getInstance();
